/**
 * mobileWallet.js — Seul point de vérité pour la détection mobile et les deep links wallet.
 *
 * Exports :
 *   isMobile()                — détecte un vrai appareil mobile
 *   getInjectedWallet()       — id du wallet injecté (mobile uniquement)
 *   getMobileDeepLink(id)     — deep link natif pour ouvrir le wallet avec l'URL courante
 *   getMobileStoreLink(id)    — lien App Store / Play Store pour installer le wallet
 *   MOBILE_SUPPORTED_WALLETS  — wallets affichés sur mobile (deep link disponible)
 *
 * Aucune dépendance externe.
 */

/**
 * isMobile() — détecte un vrai appareil mobile (pas un desktop).
 * UserAgent + touch points pour éviter les faux positifs DevTools.
 * @returns {boolean}
 */
export function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && window.innerWidth < 1024;
}

/**
 * getInjectedWallet() — retourne l'id du wallet actuellement injecté dans le browser,
 * null si aucun ou si desktop.
 * Ordre de priorité : MetaMask > Trust > Coinbase > Binance
 * @returns {'metaMask'|'trustWallet'|'coinbaseWallet'|'binanceWallet'|null}
 */
export function getInjectedWallet() {
  if (!isMobile()) return null;
  if (typeof window === 'undefined') return null;

  if (window.ethereum?.isMetaMask)                                       return 'metaMask';
  if (window.trustwallet || window.ethereum?.isTrust)                    return 'trustWallet';
  if (window.coinbaseWalletExtension || window.ethereum?.isCoinbaseWallet) return 'coinbaseWallet';
  if (window.BinanceChain)                                               return 'binanceWallet';

  return null;
}

/**
 * getMobileDeepLink(walletId) — retourne le deep link natif pour ouvrir le wallet
 * avec l'URL courante de la marketplace.
 * Binance n'a pas de scheme fiable → retourne null.
 * @param {'metaMask'|'trustWallet'|'coinbaseWallet'|'binanceWallet'} walletId
 * @returns {string|null}
 */
export function getMobileDeepLink(walletId) {
  const encodedUrl = encodeURIComponent(window.location.href);
  const host       = window.location.hostname;
  const path       = window.location.pathname;

  switch (walletId) {
    case 'metaMask':
      return `https://metamask.app.link/dapp/${host}${path}`;
    case 'trustWallet':
      return `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedUrl}`;
    case 'coinbaseWallet':
      return `https://go.cb-wallet.com/dapp?url=${encodedUrl}`;
    case 'binanceWallet':
      return null;
    default:
      return null;
  }
}

/**
 * getMobileStoreLink(walletId) — retourne le lien App Store (iOS) ou Play Store (Android)
 * pour installer le wallet. Toujours disponible pour tous les wallets supportés.
 * @param {'metaMask'|'trustWallet'|'coinbaseWallet'|'binanceWallet'} walletId
 * @returns {string|null}
 */
export function getMobileStoreLink(walletId) {
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  switch (walletId) {
    case 'metaMask':
      return isIos
        ? 'https://apps.apple.com/app/metamask/id1438144202'
        : 'https://play.google.com/store/apps/details?id=io.metamask';
    case 'trustWallet':
      return isIos
        ? 'https://apps.apple.com/app/trust-crypto-bitcoin-wallet/id1288339409'
        : 'https://play.google.com/store/apps/details?id=com.wallet.crypto.trustapp';
    case 'coinbaseWallet':
      return isIos
        ? 'https://apps.apple.com/app/coinbase-wallet/id1278383455'
        : 'https://play.google.com/store/apps/details?id=org.toshi';
    case 'binanceWallet':
      return isIos
        ? 'https://apps.apple.com/app/binance-buy-bitcoin-crypto/id1436799971'
        : 'https://play.google.com/store/apps/details?id=com.binance.dev';
    default:
      return null;
  }
}

/**
 * Liste des wallets affichés sur mobile (deep link disponible).
 * Binance exclu (pas de deep link stable pour dApps tierces).
 * @type {string[]}
 */
export const MOBILE_SUPPORTED_WALLETS = ['metaMask', 'trustWallet', 'coinbaseWallet'];
